import { Link, UserSearch } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ResourceDraft } from "../ResourceWizard";
import { FieldLabel } from "./WizardUi";

type Props = {
  draft: ResourceDraft;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  showModerationHint?: boolean;
};

type SummaryRow = {
  icon:
    | LucideIcon
    | ((props: { className?: string; "aria-hidden"?: true }) => JSX.Element);
  value: string;
};

export function Step5Preview({ draft, onSubmit }: Props) {
  const rows = summaryRows(draft);

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <FieldLabel>проверьте данные</FieldLabel>
      <div className="tp-resource-summary-card">
        {rows.map(({ icon: Icon, value }, index) => (
          <div key={`${value}-${index}`} className="tp-resource-summary-row">
            <Icon className="tp-resource-summary-icon" aria-hidden={true} />
            <span className="tp-resource-summary-value">{value}</span>
          </div>
        ))}
      </div>
      <button onClick={onSubmit} className="tp-button-primary w-full">
        далее
      </button>
    </div>
  );
}

function summaryRows(draft: ResourceDraft): SummaryRow[] {
  const rows: SummaryRow[] = [
    { icon: TextInitialIcon, value: draft.name },
    { icon: TelegramIcon, value: resourceValue(draft) },
  ];

  const action = actionValue(draft);
  if (action) rows.push({ icon: Link, value: action });

  rows.push({ icon: UserSearch, value: verificationValue(draft) });
  return rows;
}

function resourceValue(draft: ResourceDraft): string {
  if (draft.verification_mode === "no_check" && draft.type !== "bot") {
    return `ресурс без проверки: ${draft.url}`;
  }
  if (draft.type === "bot") return `бот: ${draft.url}`;
  if (draft.type.startsWith("chat_")) return `чат: ${draft.url}`;
  return `канал: ${draft.url}`;
}

function actionValue(draft: ResourceDraft): string | null {
  if (draft.verification_mode === "no_check" && draft.type !== "bot") {
    return null;
  }
  if (draft.type === "bot") {
    return draft.bot_launch_mode === "unique_start"
      ? "запуск /start уникального пользователя"
      : "запуск /start";
  }
  return draft.target_action === "join_request"
    ? "ссылка с заявками"
    : "ссылка без заявок";
}

function verificationValue(draft: ResourceDraft): string {
  if (draft.verification_mode === "no_check" && draft.type !== "bot") {
    return "без проверки";
  }
  if (draft.type === "bot") {
    return draft.bot_verification_method === "token"
      ? "проверка через токен"
      : "проверка через сервис botmembers";
  }
  return "проверка через системного бота";
}

function TextInitialIcon({
  className,
  "aria-hidden": ariaHidden,
}: {
  className?: string;
  "aria-hidden"?: boolean;
}) {
  return (
    <svg
      className={className}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 5h6" />
      <path d="M15 12h6" />
      <path d="M3 19h18" />
      <path d="m3 12 3.553-7.724a.5.5 0 0 1 .894 0L11 12" />
      <path d="M3.92 10h6.16" />
    </svg>
  );
}

function TelegramIcon({
  className,
  "aria-hidden": ariaHidden,
}: {
  className?: string;
  "aria-hidden"?: boolean;
}) {
  return (
    <svg
      className={className}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 10l-4 4l6 6l4 -16l-18 7l4 2l2 6l3 -4" />
    </svg>
  );
}
